import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  StepHeader,
  OnboardingButton,
  OnboardingSkipButton,
  OnboardingSelect,
  type SelectOption,
} from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useSaveLanguages } from '@/hooks/api/useLeadOnboarding'
import { ArrowRightIcon } from '@untitledui-icons/react/line'
import { getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'

// Supported languages for Bonnie
const BONNIE_LANGUAGES = [
  { code: 'nl-NL', isDefault: true },
  { code: 'en-GB', isDefault: true },
  { code: 'en-US' },
  { code: 'de-DE' },
  { code: 'fr-FR' },
  { code: 'fr-CA' },
  { code: 'es-ES' },
  { code: 'es-MX' },
  { code: 'it-IT' },
  { code: 'pt-PT' },
  { code: 'pt-BR' },
  { code: 'pl-PL' },
  { code: 'da-DK' },
  { code: 'sv-SE' },
  { code: 'no-NO' },
]

interface LanguagesStepProps {
  onNext: () => void
  onBack: () => void
  onSkip?: () => void
}

export function LanguagesStep({ onNext, onBack, onSkip }: LanguagesStepProps) {
  const { t, i18n } = useTranslation()
  const { primaryLanguage, additionalLanguages, setLanguages, leadId } = useOnboardingStore()
  const [primary, setPrimary] = useState(primaryLanguage || 'nl-NL')
  const [secondary, setSecondary] = useState<string>(additionalLanguages[0] || 'en-GB')
  const [isSaving, setIsSaving] = useState(false)
  
  const saveLanguages = useSaveLanguages()

  // Build language options using the languageFlags utilities
  const languages: SelectOption<string>[] = useMemo(() => {
    const uiLocale = i18n.language || 'en'
    return BONNIE_LANGUAGES.map((lang) => ({
      value: lang.code,
      label: getLanguageDisplayName(lang.code, uiLocale) || lang.code,
      icon: getLanguageFlag(lang.code),
      isDefault: lang.isDefault,
    }))
  }, [i18n.language])

  // Filter out the primary language from secondary options
  const secondaryOptions = useMemo(
    () => languages.filter((l) => l.value !== primary),
    [primary, languages]
  )

  const handleContinue = async () => {
    const additional = secondary ? [secondary] : []
    setLanguages(primary, additional)
    
    // Save to backend
    if (leadId) {
      setIsSaving(true)
      try {
        await saveLanguages.mutateAsync({
          leadId,
          data: {
            primary_language: primary,
            additional_languages: additional,
          },
        })
      } catch (error) {
        console.error('Failed to save languages:', error)
        // Continue anyway
      } finally {
        setIsSaving(false)
      }
    }
    
    onNext()
  }

  const handleSkip = async () => {
    // Set default languages when skipping
    const defaultPrimary = 'nl-NL'
    const defaultAdditional = ['en-GB']
    setLanguages(defaultPrimary, defaultAdditional)
    
    // Save defaults to backend
    if (leadId) {
      try {
        await saveLanguages.mutateAsync({
          leadId,
          data: {
            primary_language: defaultPrimary,
            additional_languages: defaultAdditional,
          },
        })
      } catch (error) {
        console.error('Failed to save languages:', error)
      }
    }
    
    if (onSkip) {
      onSkip()
    }
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <StepHeader
        title={t('resources.signup.languages.title')}
        subtitle={t('resources.signup.languages.subtitle')}
        onBack={onBack}
      />

      <div className="flex flex-col gap-4">
        <OnboardingSelect
          value={primary}
          onChange={setPrimary}
          label={t('resources.signup.languages.primary_label')}
          options={languages}
          showDefaultBadge
        />

        <OnboardingSelect
          value={secondary}
          onChange={setSecondary}
          label={t('resources.signup.languages.secondary_label')}
          options={secondaryOptions}
          showDefaultBadge
        />
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-6 lg:mt-8 lg:pt-0">
        <OnboardingButton 
          onClick={handleContinue} 
          iconTrailing={ArrowRightIcon}
          isLoading={isSaving}
        >
          {t('resources.signup.common.continue')}
        </OnboardingButton>

        {onSkip && (
          <OnboardingSkipButton onClick={handleSkip} disabled={isSaving}>
            {t('resources.signup.common.skip_later')}
          </OnboardingSkipButton>
        )}
      </div>
    </div>
  )
}
